import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
} from "@mui/material";
import {
  Security,
  CheckCircle,
  Warning,
  Add,
  ExpandMore,
  Cancel,
  Delete,
  ErrorOutline,
  Business as BusinessIcon,
  LocationOn,
  Numbers,
  Group,
  Block,
  VpnKey,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import TSSInstructions from "./TSSInstructions";
import TSSCreationModal from "./TSSCreationModal";
import {
  GetAllTssOrderByEnum,
  UpdateTssDtoStateEnum,
} from "../../../../../api-client";
import { useGetAllTss } from "../../../../../stores/Taxation/taxation.queries";
import {
  useUpdateTss,
  useChangeAdminPin,
} from "../../../../../stores/Taxation/taxation.mutations";

const TSSSettings: React.FC = React.memo(() => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [orderBy, setOrderBy] = useState<GetAllTssOrderByEnum | undefined>(
    undefined,
  );
  const [changePinDialogOpen, setChangePinDialogOpen] = useState(false);
  const [disableConfirmOpen, setDisableConfirmOpen] = useState(false);
  const [selectedTssId, setSelectedTssId] = useState<string>("");
  const [newPin, setNewPin] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Use Tanstack Query to fetch TSS data
  const { data: tssList = [], isLoading, error } = useGetAllTss(orderBy);

  // Mutations
  const updateTssMutation = useUpdateTss();
  const changeAdminPinMutation = useChangeAdminPin();

  const handleCreateTSS = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleTSSCreated = () => {
    // Query will automatically refetch due to invalidation in mutation
    // console.log("TSS created successfully!");
  };

  const handleDisableTss = (tssId: string) => {
    setSelectedTssId(tssId);
    setDisableConfirmOpen(true);
  };

  const handleConfirmDisable = () => {
    if (!selectedTssId) return;

    updateTssMutation.mutate({
      tssId: selectedTssId,
      state: UpdateTssDtoStateEnum.Disabled,
    });
    setDisableConfirmOpen(false);
    setSelectedTssId("");
  };

  const handleCancelDisable = () => {
    setDisableConfirmOpen(false);
    setSelectedTssId("");
  };

  const handleOpenChangePinDialog = (tssId: string) => {
    setSelectedTssId(tssId);
    setChangePinDialogOpen(true);
  };

  const handleCloseChangePinDialog = () => {
    setChangePinDialogOpen(false);
    setSelectedTssId("");
    setNewPin("");
    setShowPassword(false);
  };

  const handleChangeAdminPin = () => {
    if (!selectedTssId || !newPin) return;

    changeAdminPinMutation.mutate(
      {
        tssId: selectedTssId,
        dto: { newPin },
      },
      {
        onSuccess: () => {
          handleCloseChangePinDialog();
        },
      },
    );
  };

  const getStatusChip = (status: string) => {
    const statusConfig: Record<
      string,
      {
        label: string;
        bgcolor: string;
        color: string;
      }
    > = {
      INITIALIZED: {
        label: "Initialisiert",
        bgcolor: "#6FBF99", // Green
        color: "#fff",
      },
      UNINITIALIZED: {
        label: "Nicht initialisiert",
        bgcolor: "#ff9800", // Orange
        color: "#fff",
      },
      CREATED: {
        label: "Erstellt",
        bgcolor: "#4361FF", // Blue
        color: "#fff",
      },
      DISABLED: {
        label: "Deaktiviert",
        bgcolor: "#9e9e9e", // Grey
        color: "#fff",
      },
      DELETED: {
        label: "Gelöscht",
        bgcolor: "#f44336", // Red
        color: "#fff",
      },
      DEFECTIVE: {
        label: "Defekt",
        bgcolor: "#d32f2f", // Dark Red
        color: "#fff",
      },
      EVICTED: {
        label: "Entfernt",
        bgcolor: "#e91e63", // Pink
        color: "#fff",
      },
    };

    const config = statusConfig[status] || {
      label: "Unbekannt",
      bgcolor: "#f44336",
      color: "#fff",
    };

    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          bgcolor: config.bgcolor,
          color: config.color,
          fontWeight: 600,
        }}
      />
    );
  };

  return (
    <Box sx={{ height: "100%", overflow: "auto", pb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            mb: 1,
            fontWeight: 600,
            background: "#003059",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          Technische Sicherheitseinrichtung (TSE)
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Verwalten Sie Ihre TSS-Konfiguration und Sicherheitseinstellungen
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* Main Content */}
        <Grid item xs={12} lg={9}>
          {/* Action Bar */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Button
                variant="contained"
                size="small"
                startIcon={<Add />}
                onClick={handleCreateTSS}
                sx={{
                  bgcolor: "#bbcde9",
                  "&:hover": {
                    bgcolor: "#003059",
                  },
                  minWidth: { xs: "100%", sm: "auto" },
                }}
              >
                Neue TSS
              </Button>

              {/* Filters */}
              <Stack direction="row" spacing={2}>
                <FormControl size="small" sx={{ minWidth: 150 }}>
                  <InputLabel>Sortieren nach</InputLabel>
                  <Select
                    value={orderBy || ""}
                    label="Sortieren nach"
                    onChange={(e) =>
                      setOrderBy(
                        e.target.value
                          ? (e.target.value as GetAllTssOrderByEnum)
                          : undefined,
                      )
                    }
                  >
                    <MenuItem value="">
                      <em>Standard</em>
                    </MenuItem>
                    <MenuItem value={GetAllTssOrderByEnum.Description}>
                      Beschreibung
                    </MenuItem>
                    <MenuItem value={GetAllTssOrderByEnum.State}>
                      Status
                    </MenuItem>
                    <MenuItem value={GetAllTssOrderByEnum.TimeCreation}>
                      Erstellungsdatum
                    </MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Stack>
          </Paper>

          {/* TSS List */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              Fehler beim Laden der TSS-Konfigurationen
            </Alert>
          )}

          {isLoading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CircularProgress size={48} />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Lade TSS-Konfigurationen...
              </Typography>
            </Box>
          ) : tssList.length > 0 ? (
            <Box
              sx={{
                maxHeight: "calc(100vh - 350px)",
                overflowY: "auto",
                pr: 1,
                "&::-webkit-scrollbar": {
                  width: "8px",
                },
                "&::-webkit-scrollbar-track": {
                  background: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                  background: "#bdbdbd",
                  borderRadius: "4px",
                  "&:hover": {
                    background: "#757575",
                  },
                },
              }}
            >
              {tssList.map((tss, index) => (
                <Accordion
                  key={tss._id}
                  sx={{
                    mb: 1,
                    "&:before": { display: "none" },
                    boxShadow: 1,
                    borderRadius: 1,
                    overflow: "hidden",
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#003059",
                      boxShadow: 3,
                    },
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMore fontSize="small" />}
                    sx={{
                      minHeight: 48,
                      py: 0.5,
                      bgcolor: index % 2 === 0 ? "#ffffff" : "#f5f5f5",
                      "&:hover": {
                        bgcolor: "#d6ebfa",
                        "& .MuiTypography-root": {
                          color: "#1565c0",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "#1565c0",
                        },
                      },
                      "& .MuiAccordionSummary-content": {
                        my: 0.5,
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={2}
                      alignItems="center"
                      sx={{ width: "100%" }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          bgcolor: "#003059",
                          color: "#fff",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        {index + 1}
                      </Box>
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ flexGrow: 1, justifyContent: "space-evenly" }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center">
                          <BusinessIcon fontSize="small" color="inherit" />
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.875rem" }}
                          >
                            <strong>Geschäft:</strong>{" "}
                            {tss.metadata?.business || "N/A"}
                          </Typography>
                        </Stack>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <LocationOn fontSize="small" color="inherit" />
                          <Typography
                            variant="body2"
                            sx={{ fontSize: "0.875rem" }}
                          >
                            <strong>Standort:</strong>{" "}
                            {tss.metadata?.location || "N/A"}
                          </Typography>
                        </Stack>
                      </Stack>
                      {getStatusChip(tss.state)}
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails
                    sx={{
                      bgcolor: "grey.50",
                      p: 3,
                      borderTop: "2px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Stack spacing={2} sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                        color="text.primary"
                        sx={{ fontWeight: 600 }}
                      >
                        TSS-ID:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontFamily: "monospace",
                          bgcolor: "background.paper",
                          p: 1.5,
                          borderRadius: 1,
                          wordBreak: "break-all",
                        }}
                      >
                        {tss._id}
                      </Typography>
                      {tss.description && (
                        <>
                          <Typography
                            variant="subtitle1"
                            color="text.primary"
                            sx={{ fontWeight: 600 }}
                          >
                            Beschreibung:
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              bgcolor: "background.paper",
                              p: 1.5,
                              borderRadius: 1,
                              wordBreak: "break-all",
                            }}
                          >
                            {tss.description}
                          </Typography>
                        </>
                      )}
                    </Stack>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                      {/* Transaction Counter */}
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, height: "100%" }}>
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Numbers fontSize="small" color="inherit" />
                              <Typography
                                variant="subtitle2"
                                color="text.primary"
                              >
                                Transaktionszähler
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              color="info.main"
                            >
                              {tss.transaction_counter ?? 0}
                            </Typography>
                          </Stack>
                        </Paper>
                      </Grid>

                      {/* Registered Clients */}
                      <Grid item xs={12} sm={6}>
                        <Paper sx={{ p: 2, height: "100%" }}>
                          <Stack spacing={1}>
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              <Group fontSize="small" color="inherit" />
                              <Typography
                                variant="subtitle2"
                                color="text.primary"
                              >
                                Initialization Zeit
                              </Typography>
                            </Stack>
                            <Typography
                              variant="body1"
                              fontWeight={400}
                              color="inherit"
                            >
                              {tss.time_init_formatted || "N/A"}
                            </Typography>
                          </Stack>
                        </Paper>
                      </Grid>

                      {/* Environment */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="caption" color="text.secondary">
                            Umgebung: <strong>{tss._env}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Status: {tss.state}
                          </Typography>
                        </Stack>
                      </Grid>

                      {/* Action Buttons */}
                      <Grid item xs={12}>
                        <Divider sx={{ my: 2 }} />
                        <Stack
                          direction="row"
                          spacing={2}
                          justifyContent="flex-end"
                        >
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<VpnKey />}
                            onClick={() => handleOpenChangePinDialog(tss._id)}
                            disabled={
                              changeAdminPinMutation.isPending ||
                              tss.state === "DISABLED"
                            }
                            sx={{
                              borderColor: "#003059",
                              color: "#003059",
                              "&:hover": {
                                borderColor: "#001f3f",
                                bgcolor: "#003059",
                              },
                            }}
                          >
                            Admin-PIN ändern
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<Block />}
                            onClick={() => handleDisableTss(tss._id)}
                            disabled={
                              updateTssMutation.isPending ||
                              tss.state === "DISABLED"
                            }
                            sx={{
                              bgcolor: "#f44336",
                              "&:hover": {
                                bgcolor: "#d32f2f",
                              },
                            }}
                          >
                            TSS deaktivieren
                          </Button>
                        </Stack>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          ) : (
            <Paper sx={{ p: 6, textAlign: "center" }}>
              <Security sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
              <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                Keine TSS-Konfigurationen gefunden
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ mb: 3 }}>
                Erstellen Sie Ihre erste TSS, um mit sicheren Transaktionen zu
                beginnen
              </Typography>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={handleCreateTSS}
              >
                Jetzt TSS erstellen
              </Button>
            </Paper>
          )}
        </Grid>

        {/* Right Column - Instructions */}
        <Grid item xs={12} lg={3}>
          <TSSInstructions />
        </Grid>
      </Grid>

      {/* TSS Creation Modal */}
      <TSSCreationModal
        open={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleTSSCreated}
      />

      {/* Change Admin PIN Dialog */}
      <Dialog
        open={changePinDialogOpen}
        onClose={handleCloseChangePinDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Admin-PIN ändern</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Geben Sie eine neue Admin-PIN für diese TSS ein.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Neue PIN"
            type={showPassword ? "text" : "password"}
            fullWidth
            variant="outlined"
            value={newPin}
            onChange={(e) => setNewPin(e.target.value)}
            placeholder="Neue Admin-PIN eingeben"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseChangePinDialog} color="inherit">
            Abbrechen
          </Button>
          <Button
            onClick={handleChangeAdminPin}
            variant="contained"
            disabled={!newPin || changeAdminPinMutation.isPending}
            sx={{
              bgcolor: "#003059",
              "&:hover": {
                bgcolor: "#001f3f",
              },
            }}
          >
            {changeAdminPinMutation.isPending ? "Ändern..." : "PIN ändern"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disable TSS Confirmation Dialog */}
      <Dialog
        open={disableConfirmOpen}
        onClose={handleCancelDisable}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: "#f44336" }}>
          TSS deaktivieren bestätigen
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Achtung: Diese Aktion kann nicht rückgängig gemacht werden!
          </Alert>
          <Typography variant="body2" color="text.secondary">
            Sind Sie sicher, dass Sie diese TSS deaktivieren möchten? Nach der
            Deaktivierung kann die TSS nicht mehr für Transaktionen verwendet
            werden.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelDisable} color="inherit">
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmDisable}
            variant="contained"
            disabled={updateTssMutation.isPending}
            sx={{
              bgcolor: "#f44336",
              "&:hover": {
                bgcolor: "#d32f2f",
              },
            }}
          >
            {updateTssMutation.isPending
              ? "Deaktivieren..."
              : "Ja, deaktivieren"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

TSSSettings.displayName = "TSSSettings";

export default TSSSettings;
